import {MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import {addDays} from 'date-fns'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ImgButton} from '../../components/core/button'
import Dropdown from '../../components/core/dropdown'
import FiltersDropdown from '../../components/core/filters/FiltersDropdown'
import Searchbar from '../../components/core/filters/Searchbar'
import SelectedActions from '../../components/core/filters/SelectedActions'
import {PageSubTitle, PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import IncidentModal from '../../components/pages/incidents/IncidentModal'
import IncidentsTable from '../../components/pages/incidents/IncidentsTable'
import IncidentTabs from '../../components/pages/incidents/IncidentTabs'
import {
    getActionButtonStatus,
    getStatusOptionsWithIcons,
    handleStatusUpdate
} from '../../components/pages/incidents/statusOptions'
import TeamSwitcher from '../../components/pages/teams'
import {incidentsFilterConfig} from '../../configs/incidentsFilterConfig'
import {EditIcon, PlusIcon, UserIcon, UsersIcon} from '../../icons'
import {getJobs} from '../../stores/heartbeats'
import {getIncidentActions, getIncidents, setIncidents, setIsFetching} from '../../stores/incident-management'
import {getAllActiveMembers} from '../../stores/members'
import {setRangeSelected} from '../../stores/monitoring'
import {getResources, getServerResources} from '../../stores/resources'
import {getStatuspages, getStatuspagesComponents} from '../../stores/statuspage'
import {getTeams} from '../../stores/teams'
import {colors} from '../../utils/colors'
import Customer from '../../utils/customer'
import {envConfig} from '../../utils/env'
import {useFilters} from '../../utils/hooks/useFilters'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {sortBySelected} from '../../utils/sorting'
import './index.scss'
import {useQueryParams} from '../../utils/hooks/useQueryParams'
import useInterval from 'use-interval'

const assigneeIcons = {
    team: <UsersIcon color={colors.colorPrimary} className="table-icon"/>,
    customer: <UserIcon color={colors.colorPrimary} className="table-icon"/>
}

const getIconByAssigneeType = (type) => assigneeIcons[type]

const Incidents = () => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('incidents')
    const date = new Date()
    const dropdownMenuRef = useRef()

    const {isReadOnlyModeEnabled, liveUpdateTime} = envConfig
    const incidents = useSelector(state => state.incident_management.incidents)
    const incidentActions = useSelector(state => state.incident_management.incidentActions)
    const statuspages = useSelector(state => state.statuspage.statuspages)
    const statuspageComponents = useSelector(state => state.statuspage.statuspageComponents)
    const isFetching = useSelector(state => state.incident_management.isFetching)
    const isLoadMoreEnabled = useSelector(state => state.incident_management.isLoadMoreEnabled)
    const isButtonLoaderActive = useSelector(state => state.incident_management.isButtonLoaderActive)
    const isProcessing = useSelector(state => state.incident_management.isProcessing)
    const teams = useSelector(state => state.teams.teams)
    const teamId = useSelector(state => state.teams.selectedTeamId)
    const isRangeSelected = useSelector(state => state.monitoring.rangeSelected)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)

    const [allMembers, setAllMembers] = useState([])
    const filtersConfig = incidentsFilterConfig(text, teams, allMembers)
    const filtersConfigValues = useMemo(() => Object.values(filtersConfig), [filtersConfig, text])

    const [isIncidentPopupActive, setIsIncidentPopupActive] = useState(false)
    const [checkedIncidentsIds, setCheckedIncidentsIds] = useState([])
    const [isEdit, setIsEdit] = useState(false)
    const [isTabClicked, setIsTabClicked] = useState(false)

    const checkedIncident = incidents?.find(item => item?.id === checkedIncidentsIds[0])

    const initialFilters = useMemo(() => ({
        search: '',
        statuses: filtersConfig.statuses.props.options,
        urgencies: filtersConfig.urgencies.props.options,
        range: [{startDate: date, endDate: addDays(date, 0), key: 'selection'}],
        resource_type: text.resource_options.all,
        teams: teams?.filter(team => team?.team_id === teamId) || [],
        members: [],
        resource_name: ''
    }), [text, teams, filtersConfig])

    const {
        filteredData,
        filterSettings,
        setFilterSettings,
        resetFilterSettings,
        saveTempFilters,
        applyTempFilters,
        restorePreviousFilters
    } = useFilters(initialFilters, incidents, filtersConfigValues, text, isRangeSelected)

    const {params, hasParams, getParam, clearParams} = useQueryParams()
    const statusOptions = getStatusOptionsWithIcons(text)
    const [sortedIncidents, setSortedIncidents] = useState([filteredData])

    const actions = useMemo(() => [
        {
            type: 'select',
            label: text.choose_status,
            props: {
                options: getStatusOptionsWithIcons(text, true)?.filter(
                    (item) => checkedIncident?.status !== item?.value
                )
            },
            disabled: getActionButtonStatus(checkedIncident, checkedIncidentsIds, text),
            onChange: (status) => handleStatusUpdate(
                incidentActions,
                statuspageComponents,
                status.value,
                checkedIncident,
                setCheckedIncidentsIds,
                teamId,
                dispatch,
                text
            )
        },
        {
            type: 'button',
            label: '',
            icon: <PlusIcon />,
            className: 'responsive-add-button',
            disabled: isReadOnlyModeEnabled,
            onClick: () => handleModal()
        },
        {
            type: 'button',
            label: text.edit,
            icon: <EditIcon />,
            disabled: getActionButtonStatus(checkedIncident, checkedIncidentsIds, text) || isReadOnlyModeEnabled,
            onClick: () => handleModal(true, checkedIncident?.id)
        }
    ], [text, checkedIncident, checkedIncidentsIds, isReadOnlyModeEnabled, incidentActions, statuspageComponents, teamId, dispatch])

    const filterActions = {
        apply: () => {
            setIsTabClicked(false)
            dropdownMenuRef?.current?.closeMenu()
            setSortedIncidents(sortBySelected(filteredData, 'incidents'))
        },
        reset: () => {
            setIsTabClicked(false)
            dropdownMenuRef?.current?.closeMenu()
            resetFilterSettings()
            clearParams()
            setSortedIncidents(sortBySelected(incidents, 'incidents'))
            dispatch(setRangeSelected(false))
        },
        clearResource: () => {
            clearParams('resource_name')
            setFilterSettings({...filterSettings, resource_name: ''})
        }
    }

    const getQueryResources = async(resourceType, resourceId) => {
        const map = {
            [text.resource_options.resources.type]: async() => {
                const data = await dispatch(getResources(true, true))
                return data.map(i => ({...i, id: i.resource_id}))
            },
            [text.resource_options.server_resources.type]: async() =>
                await dispatch(getServerResources(false, true)),
            [text.resource_options.heartbeats.type]: async() => {
                const data = await dispatch(getJobs())
                return data.map(i => ({...i, id: i.heartbeat_id}))
            }
        }

        const fetchFn = map[resourceType]
        if (!fetchFn) return []

        const resources = await fetchFn()
        const matched = resources.find(r => r.id === resourceId)

        setFilterSettings(prev => ({
            ...prev,
            resource_type: filtersConfig.resource_type.props.options.find(r => r.type === resourceType),
            resource_name: matched?.name || ''
        }))

        return resources
    }

    const handleModal = (isEdit = false, id = '') => {
        setIsEdit(isEdit)
        setIsIncidentPopupActive(!isIncidentPopupActive)
        isEdit && !!id ? setCheckedIncidentsIds([id]) : setCheckedIncidentsIds([])
    }

    const handleRangeChange = (date) => {
        const startDate = date?.[0]?.startDate
        const endDate = date?.[0]?.endDate
        setFilterSettings({
            ...filterSettings,
            range: [{startDate, endDate, key: 'selection'}]
        })
        dispatch(setRangeSelected(true))
    }

    const loadMoreIncidents = () => {
        const lastCreatedAt = incidents?.reduce((prev, current) => {
            const prevDate = new Date(prev?.created_at)
            const currentDate = new Date(current?.created_at)
            return prevDate < currentDate ? prev : current
        })?.created_at
        dispatch(getIncidents(teamId, lastCreatedAt, false, true))
    }

    useEffect(() => {
        dispatch(setIsFetching(true))
        dispatch(setRangeSelected(false))
        if (!Customer.isRoot()) dispatch(getTeams())
    }, [])

    useEffect(() => {
        if (!teams?.length) return
        dispatch(getAllActiveMembers(teams)).then(setAllMembers)
        dispatch(getStatuspages())
        setFilterSettings({
            ...filterSettings,
            teams: teams.filter(team => team?.team_id === teamId)
        })
    }, [teamId, teams])

    useEffect(() => {
        if (!isIncidentPopupActive) setIsEdit(false)
    }, [isIncidentPopupActive])

    useEffect(() => {
        setSortedIncidents(sortBySelected(filteredData, 'incidents'))
    }, [incidents, teams, allMembers, filterSettings.search, isTabClicked])

    useEffect(() => {
        checkedIncident?.id && dispatch(getIncidentActions(checkedIncident?.id))
    }, [checkedIncident, isEdit])

    useEffect(() => {
        if (statuspages?.length > 0) dispatch(getStatuspagesComponents(statuspages))
    }, [statuspages])

    useEffect(() => {
        const resourceId = getParam('resource_id')
        const resourceType = getParam('resource_type')
        const lastCreatedAt = getParam('last_created_at')

        if (!resourceType) {
            dispatch(getIncidents(teamId))
            return
        }

        getQueryResources(resourceType, resourceId).then(() => {
            dispatch(getIncidents(teamId, lastCreatedAt, false, false, resourceType, resourceId))
        })
    }, [params.toString(), teamId])

    useInterval(async() => {
        const resourceType = getParam('resource_type')
        const resourceId = getParam('resource_id')
        const lastCreatedAt = getParam('last_created_at')
        const newIncidents = await dispatch(getIncidents(teamId, lastCreatedAt, false, false, resourceType, resourceId))
        const updatedIncidents = [
            ...newIncidents,
            ...incidents.slice(newIncidents.length)
        ]?.filter(Boolean)
        dispatch(setIncidents({data: updatedIncidents}))
    }, liveUpdateTime)

    const getAssignees = (assignees = []) => {
        if (assignees.length === 0) return <span>{text.no_assignee || '—'}</span>

        const [firstAssignee, ...others] = assignees
        const getNameByType = (assignee) => {
            if (assignee.type === 'team') {
                return teams.find(team => team?.value === assignee.id)?.name || text.unknown_team
            }
            const member = allMembers.find(member => member?.value === assignee.id)
            return member ? `${member.name} ${member.surname || ''}`.trim() : text.unknown_member
        }

        const icon = getIconByAssigneeType(firstAssignee.type)
        const name = getNameByType(firstAssignee)
        const remainingCount = others.length

        return (
            <div className="assignee-cell d-flex align-items-center gap-half">
                {icon}
                <span>{name}</span>
                {remainingCount > 0 && (
                    <Dropdown
                        align="start"
                        menuButton={<span className="more">{`, +${remainingCount} more`}</span>}
                    >
                        {others.map((assignee, index) => (
                            <MenuItem key={`${assignee.id}-${index}`} onClick={e => e.preventDefault()}>
                                <span className="d-flex align-items-center gap-half">
                                    {getIconByAssigneeType(assignee.type)}
                                    {getNameByType(assignee)}
                                </span>
                            </MenuItem>
                        ))}
                    </Dropdown>
                )}
            </div>
        )
    }

    return (
        <main className={classNames('main incidents', {dark: isDarkMode})}>
            <IncidentModal
                isOpen={isIncidentPopupActive}
                isEdit={isEdit}
                onClose={handleModal}
                incident={checkedIncident}
                allMembers={allMembers}
                allComponents={statuspageComponents}
                setCheckedIncidentsIds={setCheckedIncidentsIds}
                text={text}
            />
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {dark: isDarkMode})}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1 className={classNames('page-title', {dark: isDarkMode})}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    <ImgButton onClick={() => handleModal()} disabled={isReadOnlyModeEnabled}>
                        {text.add_new_incident}
                        <span><PlusIcon/></span>
                    </ImgButton>
                </div>
                {Customer.isRoot() && <TeamSwitcher/>}
                <IncidentTabs
                    filters={filterSettings}
                    setFilters={setFilterSettings}
                    isDarkMode={isDarkMode}
                    text={text}
                    statusOptions={statusOptions}
                    setIsTabClicked={setIsTabClicked}
                />
                <div className={classNames('search-filter-actions', {dark: isDarkMode})}>
                    <div className='search-actions'>
                        <Searchbar
                            placeholder={filtersConfig.search.props.placeholder}
                            isDarkMode={isDarkMode}
                            value={filterSettings.search}
                            onChange={(e) => setFilterSettings({...filterSettings, search: e.target.value})}
                        />
                        <FiltersDropdown
                            text={text}
                            isDarkMode={isDarkMode}
                            filterSettings={filterSettings}
                            setFilterSettings={setFilterSettings}
                            filterConfig={filtersConfigValues.filter(configItem => configItem.showInDropdown)}
                            dropdownMenuRef={dropdownMenuRef}
                            rangeSelected={isRangeSelected}
                            onChangeDateRange={handleRangeChange}
                            onResetFilters={filterActions.reset}
                            customAfterLabel={filterSettings.resource_name}
                            onClearCustomAfterLabel={filterActions.clearResource}
                            isFilterButtonActive={hasParams}
                            onOpenMenu={saveTempFilters}
                            onApplyFilters={() => {
                                filterActions.apply()
                                applyTempFilters()
                            }}
                            onCloseMenu={restorePreviousFilters}
                        />
                    </div>
                    <SelectedActions isDarkMode={isDarkMode} actions={actions}/>
                </div>
                <IncidentsTable
                    incidents={incidents}
                    isFetching={isFetching}
                    isLoadMoreEnabled={isLoadMoreEnabled}
                    onLoadMoreIncidents={loadMoreIncidents}
                    isButtonLoaderActive={isButtonLoaderActive}
                    isDarkMode={isDarkMode}
                    checkedIncidentsIds={checkedIncidentsIds}
                    setCheckedIncidentsIds={setCheckedIncidentsIds}
                    sortedIncidents={sortedIncidents}
                    setSortedIncidents={setSortedIncidents}
                    text={text}
                    onOpenEditModal={(updatedIncidentId) => handleModal(true, updatedIncidentId)}
                    isProcessing={isProcessing}
                    getAssigneesList={getAssignees}
                />
            </div>
            <Footer/>
        </main>
    )
}

export default Incidents
